// Privacy Policy (GDPR, BDSG, TDDDG, DSA) — the one privacy declaration of the site (the separate
// German page was merged into it on 2026-11-02; /legal/datenschutz shows this page). Describes
// exactly what the server stores (server/src/db/schema.ts, auth.ts, routes/*, services/*, live.ts,
// settingsDefaults.ts retention) and what the browser keeps locally. Keep it in step with the code.
import { Link } from "../../lib/router";
import { CONTACT, LegalLayout, LegalLink, OperatorAddress } from "./LegalLayout";

export function Privacy() {
  return (
    <LegalLayout slug="privacy" title="Privacy Policy" subtitle="Datenschutzerklärung">
      <h2 id="short">The short version</h2>
      <ul>
        <li>
          You can listen to The Infinite without an account. There is no tracking: no analytics, no ads, no profiling,
          no third-party scripts, fonts or embeds.
        </li>
        <li>
          To write songs you sign in with your email address and choose a nickname. We store what the service needs:
          your account, your drafts and songs, ratings, likes and tapes, and a few records that keep the service safe.
        </li>
        <li>
          Public are your nickname, your profile (bio and link if you add them), your songs with their titles, play
          times, Wizard marks and rating averages. Your email address is never shown.
        </li>
        <li>
          We email you about your songs: when one is approved or not, when its play time is fixed (with a calendar
          entry) and shortly before it plays. You can switch the play-time emails off on your account page.
        </li>
        <li>
          A new contributor's first songs are listened to by a person before they play. Admins can remove a song at any
          time, even while it plays, and tell you why.
        </li>
        <li>
          We use one cookie, <code>eb_session</code>, only to keep you signed in. Everything else your browser keeps is
          described in the <LegalLink to="cookies">Cookie Notice</LegalLink>.
        </li>
        <li>
          You can download all your data and delete your account at any time on the{" "}
          <Link href="/account">account page</Link>. Songs that have already played stay in the song, credited to
          “former contributor”, because you licensed them under CC BY 4.0.
        </li>
      </ul>

      <h2 id="controller">1. Who is responsible</h2>
      <p>
        The controller for the processing of personal data on The Infinite (Art. 4(7) GDPR), at the address of the
        website and its sister site The Infinite 72, is:
      </p>
      <OperatorAddress />
      <p>
        For every privacy question or request, {CONTACT}. We have not appointed a data protection officer, as we are not
        required to (Art. 37 GDPR, § 38 BDSG).
      </p>

      <h2 id="bases">2. Legal bases</h2>
      <p>We process personal data only on one of these bases, named for each purpose below:</p>
      <ul>
        <li>
          <strong>Contract</strong> (Art. 6(1)(b) GDPR): providing the service you use under our{" "}
          <LegalLink to="terms">Terms of Use</LegalLink> — your account, your songs and everything that comes with them.
        </li>
        <li>
          <strong>Legitimate interests</strong> (Art. 6(1)(f) GDPR): keeping the service secure, working and free of
          abuse and copying, moderating content, and being able to trace what happened. Each interest is named where it
          applies. You can object (see section 10).
        </li>
        <li>
          <strong>Legal obligations</strong> (Art. 6(1)(c) GDPR): for example the duties of hosting services under the
          EU Digital Services Act (DSA), keeping proof of consent, and lawful orders from authorities.
        </li>
        <li>
          <strong>Consent</strong> (Art. 6(1)(a) GDPR, § 25(1) TDDDG): only for optional browser storage of your display
          settings, which you can withdraw at any time (see section 6).
        </li>
      </ul>

      <h2 id="data">3. What we process, and why</h2>

      <h3 id="visits">3.1 Visiting and listening</h3>
      <ul>
        <li>
          <strong>Server logs.</strong> Our web server (Caddy) and the application log each request: your IP address,
          date and time, the requested address and method, the response status, size and timing, and the browser's
          user-agent string and referrer. The sign-in cookie is not logged. We use the logs to deliver the site, find
          errors and fend off attacks. Basis: Art. 6(1)(f) GDPR — a secure and working service. Logs are deleted after
          14 days, unless a specific entry is needed to investigate an attack.
        </li>
        <li>
          <strong>Rate limits.</strong> To limit how often sign-in emails, submissions, reports and other actions can be
          requested, the server counts requests per IP address or session in short windows (usually a minute) and at
          most 3 sign-in emails per address in 15 minutes. These counters live only in the server's memory and vanish
          when the window ends. Basis: Art. 6(1)(f) GDPR — preventing abuse and protecting other people's inboxes.
        </li>
        <li>
          <strong>Live connection.</strong> While a page is open it keeps a connection (WebSocket, or regular polling)
          to receive lineup changes, likes and check results. In memory only, the server knows which stream you're on,
          whether you're listening (to show the listener count) and, if you're signed in, your account ID (to send you
          your own results). No listening history is stored. Basis: Art. 6(1)(b) and (f) GDPR.
        </li>
        <li>
          <strong>Share previews.</strong> Pages of songs, days and profiles have preview images and titles for when a
          link is shared. They are made on our server from public information only (section 5). When you share a link on
          another platform, that platform fetches the preview from us under its own privacy policy.
        </li>
      </ul>

      <h3 id="signin">3.2 Signing in by email</h3>
      <p>
        There are no passwords. When you ask for a sign-in link (or register with the form), we store your{" "}
        <strong>email address</strong>, a <strong>hash</strong> (SHA-256) of the single-use token in the link (never the
        token itself), when it was created, when it expires (after 15 minutes) and when it was used, the{" "}
        <strong>IP address</strong> the request came from, and — when you register — the nickname you asked for and that
        you accepted the Terms. Then we email you the link. Basis: Art. 6(1)(b) GDPR for signing you in; Art. 6(1)(f)
        GDPR for the IP address and the per-address limit, which stop people from using us to flood someone's inbox.
        These records are deleted after 30 days.
      </p>

      <h3 id="account">3.3 Your account</h3>
      <p>
        Your account consists of a random account ID, your <strong>email address</strong>, your{" "}
        <strong>nickname</strong>, the date you joined, when you accepted the Terms (if you used the registration form),
        your settings (the look of the site you chose, and whether you want play-time emails) and, if you add them, a
        short public <strong>bio</strong> and one <strong>link</strong>. Your avatar is drawn from your nickname; no
        picture is uploaded. Nicknames, bios and song titles are checked automatically against a word list and a
        character policy. Basis: Art. 6(1)(b) GDPR. Kept until you delete your account.
      </p>

      <h3 id="sessions">3.4 Sessions and security tokens</h3>
      <p>
        Once you're signed in, your browser holds the HTTP-only cookie <code>eb_session</code> with a random token. The
        server stores only a hash of that token, your account ID, a random <strong>CSRF token</strong> (protecting you
        against forged requests from other sites) and when the session was created, last used and expires. A session
        lasts 30 days and is extended while you use it; signing out ends it at once. Basis: Art. 6(1)(b) and (f) GDPR.
        Expired sessions are deleted 7 days after they expire, and all sessions with your account.
      </p>

      <h3 id="songs">3.5 Writing and submitting songs</h3>
      <ul>
        <li>
          <strong>Drafts</strong> you save on the server: the notes and mix of the song, its title, the stream and day
          it's for, its status, a temporary place reservation and timestamps. Drafts for a day that never made it into
          the song are deleted 30 days after that day. The editor also keeps a backup of your drafts in your own browser
          (see the <LegalLink to="cookies">Cookie Notice</LegalLink>), which never leaves your device on its own.
        </li>
        <li>
          <strong>Submitted songs</strong>: the song and its title, your account ID, the stream, day and place in the
          day's lineup, the Wizard share, loudness and playback level, the time of approval and, where it applies, the
          review decision or why it was hidden or removed. When submitting you confirm that the song is your own work
          and license it under CC BY 4.0.
        </li>
        <li>
          <strong>Check results</strong>: the outcome and plain-language reason of every automatic check (section 4) and
          audio measurements of the rendered song. Results of songs that were not accepted are deleted after 90 days;
          those of accepted songs are kept with the song.
        </li>
        <li>
          <strong>Melodic fingerprints</strong>: short sequences derived from the melody lines of each accepted song,
          stored with the song, day and your account ID, so later songs can be checked against copying.
        </li>
      </ul>
      <p>
        Basis: Art. 6(1)(b) GDPR (running the song and its checks as the Terms describe) and Art. 6(1)(f) GDPR for the
        fingerprints (protecting other people's melodies and the integrity of the song).
      </p>

      <h3 id="review">3.6 Review of new contributors' songs</h3>
      <p>
        The first songs of each contributor (currently two) that pass the automatic checks are listened to by an
        administrator before they join a lineup. The administrator sees the song, its title, your nickname and the day
        it's for. We store the decision, the administrator's account ID, the time and — if the song is not accepted —
        the reason, which we send to you so you can change the song and submit it again. Basis: Art. 6(1)(f) GDPR —
        keeping the song free of abuse while the service is young and open to anyone — and Art. 6(1)(c) GDPR together
        with Art. 17 DSA (telling you the reasons for a moderation decision). Kept with the song; after a rejection, the
        song's melodic fingerprints are deleted.
      </p>

      <h3 id="notices">3.7 Play-time notices</h3>
      <ul>
        <li>
          <strong>Emails.</strong> When the lineup of your song's day is fixed (60 minutes before the day starts, UTC) —
          or when a reviewed song is placed later — we email you its exact play time with a calendar entry (an{" "}
          <code>.ics</code> file) attached, and again about 10 minutes before it plays. These emails are on by default
          because they are part of contributing; you can switch them off at any time on your{" "}
          <Link href="/account">account page</Link> or with the link in every email. To send them at the right moment,
          the server keeps a scheduled job with your account ID, the song and its time; job records are deleted 14 days
          after they ran.
        </li>
        <li>
          <strong>In the app.</strong> While you're signed in, the site asks our server for your songs' upcoming play
          times, shows a notice shortly before and while one plays, lists your songs on the account page, and marks your
          songs in the River. Nothing extra is stored for this.
        </li>
      </ul>
      <p>
        Basis: Art. 6(1)(b) GDPR (telling contributors when their contribution plays is part of the service) and Art.
        6(1)(f) GDPR (contributors want to hear their songs; the emails can be switched off).
      </p>

      <h3 id="wizard">3.8 The Wizard</h3>
      <p>
        If you ask the Wizard (the editor's built-in songwriting helper) for suggestions, each suggestion is logged with
        your account ID, the stream, day and draft, the kind of suggestion, the target sound, the suggested notes and
        the time. We need this to show honestly how much of a submitted song came from the Wizard; listeners see that
        mark. The Wizard runs entirely on our server; nothing is sent to an outside AI service. Basis: Art. 6(1)(b) and
        (f) GDPR (transparency towards listeners and other contributors). The log is deleted 30 days after the day the
        song was written for.
      </p>

      <h3 id="ratings">3.9 Ratings, likes and encores</h3>
      <p>
        When you rate a song (1 to 5 stars) or like it while it plays (a like counts as 5 stars), we store your account
        ID, the song, your stars and when you gave them. Likes given while a song plays count towards an encore (it
        plays again right after). Others only see averages, counts and encore progress — never who gave which rating.
        Ratings feed the averages on songs and profiles and the Hall of Fame, which ranks songs and creators. Basis:
        Art. 6(1)(b) GDPR (the feature you use) and Art. 6(1)(f) GDPR (fair rankings). Kept until you change or withdraw
        the rating or delete your account.
      </p>

      <h3 id="tapes">3.10 Tapes and bookmarks</h3>
      <p>
        Your tapes (playlists of up to 100 songs, with their names and order) and the Top 100 mixtapes you bookmark are
        stored with your account ID. They are private: only you see them. How often each day's Top 100 mixtape is
        started is counted as a single number per day, without any link to who played it. Basis: Art. 6(1)(b) GDPR. Kept
        until you delete them or your account.
      </p>

      <h3 id="tips">3.10a Tips (“Keep The Infinite Free”)</h3>
      <p>The site itself takes no payments and stores nothing about tips. If you choose to leave one:</p>
      <ul>
        <li>
          <strong>With Stripe</strong>, the button takes you to a payment page of Stripe Payments Europe, Ltd. (1 Grand
          Canal Street Lower, Dublin 2, Ireland), which processes your payment as its own controller under{" "}
          <a href="https://stripe.com/privacy" rel="noreferrer">
            its privacy policy
          </a>
          . We receive from Stripe what we need to keep our books: your name, email address, the amount, the date and
          the payment method type (never your full card details). Basis: Art. 6(1)(b) GDPR (the tip) and Art. 6(1)(c)
          GDPR (bookkeeping duties); kept for the statutory retention period of 10 years (§ 147 AO, § 257 HGB).
        </li>
        <li>
          <strong>In SOL on Solana or ETH on Ethereum</strong>, you send a transfer from your own wallet to our public
          address. Every transfer, with the sending wallet address and amount, is public on the blockchain and can't be
          deleted by anyone. We only see what everyone can see there and keep it for our books as above.
        </li>
      </ul>

      <h3 id="reports">3.11 Reports</h3>
      <p>
        When you report a song, we store your account ID, the song, the reason you picked, your optional note (up to 500
        characters) and the time. Your account's age decides whether the report counts towards hiding the song
        automatically (accounts must be at least 7 days old; 3 counting reports hide a song until it is re-checked). The
        contributor is not told who reported it. Basis: Art. 6(1)(f) GDPR (keeping the song free of copied, offensive or
        abusive songs) and Art. 6(1)(c) GDPR with Art. 16 DSA (notices of illegal content). If you delete your account,
        your reports stay without any link to you, so the count stays correct.
      </p>

      <h3 id="removal">3.12 Removal by administrators</h3>
      <p>
        Administrators can take a song out of the stream at any time, also while it plays. We store that it was removed,
        the reason, and which administrator did it (in the audit log), and email the contributor the reason. Basis: Art.
        6(1)(f) GDPR (moderation) and Art. 6(1)(c) GDPR with Art. 17 DSA (statement of reasons). If you disagree with a
        decision, {CONTACT}; a person will look at it again.
      </p>

      <h3 id="audit">3.13 Audit log</h3>
      <p>
        We log important events: accounts created and deleted, nickname changes (with the new nickname), reports, review
        decisions, songs hidden, restored or removed (with the reason), lineup changes, and administrative changes.
        Entries may contain your account ID. They let us trace what happened, defend against abuse and answer complaints
        and authorities. Basis: Art. 6(1)(f) and (c) GDPR. Entries are deleted after 365 days. After you delete your
        account, remaining entries keep the former random account ID (and, for nickname changes, the nickname) until
        they expire, but no email address.
      </p>

      <h3 id="contact">3.14 When you contact us</h3>
      <p>
        If you contact us via our official accounts, we use your message and the account name it comes from to answer
        you; the platform you write on processes it under its own privacy policy. Basis: Art. 6(1)(b) or (f) GDPR. We
        delete the conversation on our side when the matter is settled, unless the law requires us to keep it (for
        example commercial or tax records, § 257 HGB, § 147 AO).
      </p>

      <h2 id="automated">4. Automatic checks and human review</h2>
      <p>
        Every submitted song is checked automatically on our server: its structure and size, that it stays in the day's
        key and sounds, note density and clusters, loudness and noise of the rendered audio, and melodic similarity to
        earlier songs and to a list of protected melodies. Nicknames and titles are checked against a word list. The
        result decides whether the song is accepted; a new contributor's first songs are then also reviewed by a person
        (section 3.6). A song reported by 3 accounts is hidden automatically and re-checked with stricter limits.
      </p>
      <p>
        These are decisions about <em>content</em>, not about you as a person. They don't have legal or similarly
        significant effects on you within the meaning of Art. 22 GDPR: there is no profiling, every rejection comes with
        a readable reason, you can change the song and submit it again, and nothing else about your account depends on
        it. If you think a check or a review got it wrong, {CONTACT} and a person will look at it.
      </p>

      <h2 id="public">5. What is public</h2>
      <p>
        Anyone can see, without signing in: your nickname, the date you joined, your bio and link if you added them,
        your avatar (drawn from your nickname), your songs (notes, title, stream, day and play time) with their Wizard
        marks, rating averages and encores, your place in the Hall of Fame and your favourite genre (from the days you
        wrote for) — on your profile, song pages, day pages, the River and share previews. Never public: your email
        address, your drafts, the Wizard log, who rated what, your tapes and bookmarks, your reports, review notes and
        everything else in section 3.
      </p>

      <h2 id="storage">6. Cookies and storage in your browser</h2>
      <p>
        Storing information in your browser, or reading it, needs your consent unless it is strictly necessary for the
        service you asked for (§ 25 TDDDG). We use:
      </p>
      <ul>
        <li>
          <strong>Strictly necessary</strong> (no consent needed, § 25(2) no. 2 TDDDG): the sign-in cookie{" "}
          <code>eb_session</code>, the backup of your drafts in the browser's database, and your cookie choice itself (
          <code>eb.consent</code>, kept as proof of your choice, Art. 7(1) GDPR).
        </li>
        <li>
          <strong>Preferences</strong> (only with your consent, Art. 6(1)(a) GDPR): your display settings, such as the
          look of the site and which guided tours you've seen. Withdrawing consent deletes them.
        </li>
      </ul>
      <p>
        You can change or withdraw your choice at any time under “Cookie settings” at the bottom of every page; this
        doesn't affect what happened before. Details of every item, its purpose and lifetime are in the{" "}
        <LegalLink to="cookies">Cookie Notice</LegalLink>. None of it is used for tracking or shared with anyone.
      </p>

      <h2 id="recipients">7. Recipients and processors</h2>
      <ul>
        <li>
          <strong>Hosting:</strong> IONOS SE, Elgendorfer Str. 57, 56410 Montabaur, Germany, runs the server in Germany
          on which the website, the database and the backups run. It processes data only on our behalf, under a data
          processing agreement (Art. 28 GDPR).
        </li>
        <li>
          <strong>Email delivery:</strong> Namecheap, Inc. (Phoenix, Arizona, USA) sends our emails (sign-in links,
          review decisions, play times and reminders) through its Private Email service and receives your email address
          and the message, as our processor (Art. 28 GDPR). See section 8 for the transfer to the USA.
        </li>
        <li>
          <strong>Domain name:</strong> our domain is registered with Namecheap, Inc. (Phoenix, Arizona, USA), which
          also runs its name servers. Name servers only tell browsers which server address belongs to our domain; they
          are usually asked by your internet provider's resolver, not by your device, and never see what you do on the
          site.
        </li>
        <li>
          <strong>Tips:</strong> Stripe (see 3.10a) is not our processor but a separate controller for the payment you
          make on its page.
        </li>
        <li>
          <strong>Authorities</strong>, only where we are legally obliged (Art. 6(1)(c) GDPR).
        </li>
      </ul>
      <p>
        We don't sell or rent data and don't share it for advertising. Administrators of The Infinite see the review
        queue, reports, the audit log and moderation tools; they are bound to confidentiality.
      </p>

      <h2 id="transfers">8. Transfers outside the EU</h2>
      <p>
        The website, the database and the backups run on our server in Germany. One transfer leaves the EU: our email
        provider, Namecheap, Inc., is based in the USA, so the emails we send you (sign-in links, review decisions, play
        times and reminders) and your email address pass through its servers. This transfer is based on the EU standard
        contractual clauses in Namecheap's data processing agreement (Art. 46(2)(c) GDPR). Apart from that, we don't
        transfer personal data to countries outside the EU/EEA; should that change, we will update this policy first.
      </p>

      <h2 id="retention">9. How long we keep data</h2>
      <dl className="legal-dl">
        <dt>Account (email, nickname, settings, bio, link)</dt>
        <dd>Until you delete your account.</dd>
        <dt>Sessions</dt>
        <dd>
          30 days after last use at most; ended on sign-out; expired sessions deleted after 7 days; all deleted with the
          account.
        </dd>
        <dt>Sign-in link records (incl. IP address)</dt>
        <dd>Valid for 15 minutes, single use; deleted after 30 days.</dd>
        <dt>Drafts</dt>
        <dd>Until you delete them or your account; drafts that never made it into the song 30 days after their day.</dd>
        <dt>Songs that have played</dt>
        <dd>
          Permanently, as part of the song (licensed under CC BY 4.0). After account deletion they are no longer linked
          to you and are credited to “former contributor”.
        </dd>
        <dt>Songs that have not played yet</dt>
        <dd>Removed when you delete your account.</dd>
        <dt>Check results</dt>
        <dd>Of songs not accepted: 90 days. Of accepted songs: kept with the song.</dd>
        <dt>Review decisions and removal reasons</dt>
        <dd>Kept with the song; in the audit log for 365 days.</dd>
        <dt>Scheduled emails (job records)</dt>
        <dd>14 days after they ran.</dd>
        <dt>Wizard log</dt>
        <dd>30 days after the day the song was written for.</dd>
        <dt>Tips (records received from Stripe, blockchain transfers)</dt>
        <dd>10 years (statutory retention for business records).</dd>
        <dt>Ratings and likes, tapes, bookmarks</dt>
        <dd>Until you withdraw or delete them, or delete your account.</dd>
        <dt>Reports you filed</dt>
        <dd>Kept without your account link after you delete your account.</dd>
        <dt>Audit log</dt>
        <dd>365 days.</dd>
        <dt>Server logs</dt>
        <dd>14 days.</dd>
        <dt>Rate-limit counters, live connection state</dt>
        <dd>In memory only, for minutes; never written to disk.</dd>
        <dt>Backups</dt>
        <dd>
          The database is backed up daily on our server in Germany; each backup is kept for 14 days, so deleted data
          disappear from backups by then.
        </dd>
      </dl>
      <p>Where the law requires us to keep data longer, we restrict it to that purpose and delete it afterwards.</p>

      <h2 id="rights">10. Your rights</h2>
      <p>You have the right to:</p>
      <ul>
        <li>
          <strong>access</strong> your data (Art. 15 GDPR) and <strong>receive it in a portable format</strong> (Art. 20
          GDPR): the <Link href="/account">account page</Link> has a button that downloads everything stored about your
          account as a JSON file;
        </li>
        <li>
          <strong>rectification</strong> (Art. 16 GDPR): change your nickname, bio and link on the account page; for
          anything else, such as your email address, write to us;
        </li>
        <li>
          <strong>erasure</strong> (Art. 17 GDPR): delete your account on the account page (see below);
        </li>
        <li>
          <strong>restriction of processing</strong> (Art. 18 GDPR);
        </li>
        <li>
          <strong>withdraw consent</strong> at any time with effect for the future (Art. 7(3) GDPR), under “Cookie
          settings”;
        </li>
        <li>
          <strong>object</strong> to processing based on legitimate interests, on grounds relating to your particular
          situation (Art. 21(1) GDPR) — for the play-time emails, simply switch them off;
        </li>
        <li>
          <strong>lodge a complaint</strong> with a data protection supervisory authority (Art. 77 GDPR), in particular
          in the EU member state where you live or work. The authority responsible for us is the Berliner Beauftragte
          für Datenschutz und Informationsfreiheit, Alt-Moabit 59–61, 10555 Berlin, Germany (
          <a href="https://www.datenschutz-berlin.de" rel="noreferrer">
            datenschutz-berlin.de
          </a>
          ).
        </li>
      </ul>
      <p>
        To use any of these rights, {CONTACT}. We answer within one month (Art. 12(3) GDPR) and may ask you to confirm
        the request from your account's email address.
      </p>

      <h3 id="delete">What happens when you delete your account</h3>
      <ul>
        <li>
          Your account, email address, nickname, bio and link, sessions, drafts, ratings and likes, tapes, bookmarks and
          Wizard log are deleted.
        </li>
        <li>
          Songs that have <strong>already played</strong> stay in the song and its replays. You licensed them under CC
          BY 4.0, which can't be revoked, so we and others may keep using them; they are credited to “former
          contributor” instead of your nickname.
        </li>
        <li>
          Songs that have <strong>not played yet</strong> (including songs waiting for review) are removed and replaced
          by Wizard songs.
        </li>
        <li>
          Reports you made stay without any link to you. Audit entries (3.13), sign-in link records (3.2) and scheduled
          email records (3.7) are deleted at the end of their normal retention periods. Melodic fingerprints of songs
          that stay in the song are kept, because new songs are still checked against them.
        </li>
        <li>
          Copies other people have made of your songs under CC BY 4.0 are outside our control. If you want your name
          removed from such a copy, you can ask the person who made it.
        </li>
      </ul>

      <h2 id="required">11. Do you have to provide data?</h2>
      <p>
        Listening needs no personal data beyond the technical data in 3.1. To write songs, rate them or keep tapes you
        need an account, which requires an email address and a nickname; without them we can't offer these features.
      </p>

      <h2 id="children">12. Children</h2>
      <p>
        Accounts are for people aged 16 or older (see the <LegalLink to="terms">Terms of Use</LegalLink>). We don't
        knowingly process personal data of younger children. If you believe a child has created an account, please tell
        us and we'll delete it.
      </p>

      <h2 id="security">13. Security</h2>
      <p>
        All traffic is encrypted (HTTPS). Sign-in and session tokens are stored only as hashes; the session cookie is
        HTTP-only, secure and restricted to our site; state-changing requests are protected against cross-site request
        forgery; every input is validated; and a strict Content-Security-Policy stops foreign scripts from running. The
        server is kept up to date and only the necessary ports are open.
      </p>

      <h2 id="changes">14. Changes to this policy</h2>
      <p>
        We update this policy when the service or the law changes. The current version is always on this page, with its
        effective date at the top. If a change affects how we use data you already gave us, we'll tell signed-in
        contributors in the app or by email before it takes effect.
      </p>
    </LegalLayout>
  );
}
